import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import AdminHeader from "../../component/AdminHeader";
import ApiRequest from "../../helper/ApiRequest";
import { ApiEndpoints } from "../../helper/ApiEndpoints";

const emailEditEndpoint = ApiEndpoints.emailManagement.Edit;
const emailUpdateEndpoint = ApiEndpoints.emailManagement.Update;

const defaultForm = {
  form_type: "",
  to: "",
  cc: "",
  bcc: "",
};

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const parseEmailLines = (fieldName, value, required = false) => {
  const textValue = String(value || "");
  if (textValue.includes(",")) {
    return {
      error: `${fieldName} emails must be added line by line. Comma separated emails are not permitted.`,
      emails: [],
    };
  }

  const lines = textValue.split(/\r?\n/);
  const emails = lines.filter((email) => email !== "");

  if (required && !emails.length) {
    return {
      error: `At least one ${fieldName} email is required.`,
      emails: [],
    };
  }

  const emailWithSpace = emails.find((email) => email !== email.trim() || /\s/.test(email));
  if (emailWithSpace) {
    return {
      error: `Extra spaces are not valid in ${fieldName} email: ${emailWithSpace}`,
      emails: [],
    };
  }

  const invalidEmail = emails.find((email) => !emailRegex.test(email));
  if (invalidEmail) {
    return {
      error: `Invalid ${fieldName} email address: ${invalidEmail}`,
      emails: [],
    };
  }

  return { error: "", emails };
};

const validateForm = (form) => {
  if (!form.form_type.trim()) return "Form Type is required.";

  const toResult = parseEmailLines("To", form.to, true);
  if (toResult.error) return toResult.error;

  const ccResult = parseEmailLines("CC", form.cc);
  if (ccResult.error) return ccResult.error;

  const bccResult = parseEmailLines("BCC", form.bcc);
  if (bccResult.error) return bccResult.error;

  return "";
};

const buildPayload = (form) => ({
  form_type: form.form_type.trim(),
  to: parseEmailLines("To", form.to, true).emails,
  cc: parseEmailLines("CC", form.cc).emails,
  bcc: parseEmailLines("BCC", form.bcc).emails,
});

const mapApiDataToForm = (data) => ({
  form_type: data?.form_type || "",
  to: Array.isArray(data?.to) ? data.to.join("\n") : "",
  cc: Array.isArray(data?.cc) ? data.cc.join("\n") : "",
  bcc: Array.isArray(data?.bcc) ? data.bcc.join("\n") : "",
});

export default function EditEmailManagement() {
  const { dynamicemail_id } = useParams();
  const navigate = useNavigate();
  const [form, setForm] = useState(defaultForm);

  useEffect(() => {
    if (!dynamicemail_id) return;

    const dataFetch = async () => {
      try {
        const response = await ApiRequest("GET", `${emailEditEndpoint}${dynamicemail_id}`);
        setForm(mapApiDataToForm(response.dynamic_email));
      } catch (error) {
        console.log(error);
        toast.error("Unable to fetch email management record.");
      }
    };

    dataFetch();
  }, [dynamicemail_id]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setForm((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    const validationError = validateForm(form);
    if (validationError) {
      toast.error(validationError);
      return;
    }

    try {
      const response = await ApiRequest("POST", emailUpdateEndpoint, {
        dynamicemail_id,
        ...buildPayload(form),
      });
      if (response.error) {
        toast.error(response.message);
        return;
      }
      toast.success(response.message);
      navigate("/email-management");
    } catch (error) {
      console.log(error);
      toast.error(error.response?.data?.message || "Unable to update email management record.");
    }
  };

  const pagename = {
    name: "Email Management",
    user: "Admin",
    innerpage: "Email Management / Edit Email Management",
  };

  return (
    <>
      <AdminHeader pagename={pagename} />

      <div className="add-content-page">
        <form id="emailManagementEdit" onSubmit={handleSubmit}>
          <div className="add-content-wrap">
            <div className="row">
              <div className="col-lg-12">
                <div className="add-page-inputs">
                  <div className="add-page-sections">Edit Email Management</div>
                </div>
              </div>

              <div className="col-lg-6">
                <div className="add-page-inputs">
                  <div className="label-wrap-add">
                    <label>Form Type<small>*</small></label>
                  </div>
                  <input name="form_type" readOnly value={form.form_type} type="text" className="form-control" onChange={handleInputChange} required />
                </div>
              </div>

              <div className="col-lg-12">
                <div className="add-page-inputs">
                  <div className="label-wrap-add">
                    <label>To Emails<small>*</small></label>
                  </div>
                  <textarea name="to" value={form.to} className="form-control" style={{ height: "150px" }} onChange={handleInputChange} required />
                </div>
              </div>

              <div className="col-lg-6">
                <div className="add-page-inputs">
                  <div className="label-wrap-add">
                    <label>CC Emails</label>
                  </div>
                  <textarea name="cc" value={form.cc} className="form-control" style={{ height: "150px" }} onChange={handleInputChange} />
                </div>
              </div>

              <div className="col-lg-6">
                <div className="add-page-inputs">
                  <div className="label-wrap-add">
                    <label>BCC Emails</label>
                  </div>
                  <textarea name="bcc" value={form.bcc} className="form-control" style={{ height: "150px" }} onChange={handleInputChange} />
                </div>
              </div>
            </div>
          </div>
        </form>
        <button type="submit" form="emailManagementEdit" className="default-btn">publish <span></span></button>
      </div>
    </>
  );
}
